/**
 * SharedCalculation Page
 *
 * Handles shared calculation links in format: /calc/:year/:osd/:hash
 * Legacy format (TAURON default): /calc/:year/:hash
 * NEW: Shows a preview page for visitors from social media instead of immediate redirect.
 *
 * Flow:
 * 1. Extract year and hash from URL params
 * 2. Query SharedCalculation model from DynamoDB
 * 3. Calculate results and show preview
 * 4. User can view full details or start their own calculation
 *
 * @module frontend/src/pages/SharedCalculation
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Stack, Skeleton, Alert, Center, Text } from '@mantine/core';
import { IconAlertCircle, IconLoader } from '@tabler/icons-react';
import { getAmplifyClient } from '../../lib/amplifyClient';
import { useSEO } from '../../shared/hooks';
import { SharedCalculationPreview } from './components/SharedCalculation';
import { Layout } from '../../shared/components';
import type { CalculationResults, TariffType, CalculatorFormValues, HourlyConfig } from '../../types/calculator';
// Multi-OSD Support: Use same calculation approach as Calculator.tsx
import {
  calculateAnnualComparison,
  UNIFORM_PATTERN,
  type ConsumptionPattern
} from '../../../../amplify/data/utils/tariff-calculator';
import { buildOSDPricing } from '../../../../amplify/data/utils/pricing-config';
import type { OSDCode } from '../../types/osd.types';
import { getAvailableTariffsForOSD, type OSDCode as OSDMetaCode } from '../../utils/osdMetadata';

/**
 * SharedCalculation Page Component
 *
 * Shows preview of shared calculation for visitors from social media.
 */
export function SharedCalculation() {
  useSEO({
    title: 'Udostępniona kalkulacja',
    description: 'Wyniki kalkulacji taryfowej - porównanie kosztów energii dla różnych taryf.',
  });

  // Support both new format (/calc/:year/:osd/:hash) and legacy format (/calc/:year/:hash)
  const { year, osd, hash: hashParam } = useParams<{ year: string; osd?: string; hash?: string }>();

  // Handle legacy URLs: if osd looks like a hash (12 alphanumeric chars), it's actually the hash
  // New URL format: /calc/2026/ENERGA/asFJi0451T0d
  // Legacy URL format: /calc/2026/asFJi0451T0d (TAURON assumed)
  const validOSDs = ['TAURON', 'PGE', 'ENEA', 'ENERGA'] as const;
  const isOsdActuallyHash = osd && osd.length === 12 && /^[A-Za-z0-9]{12}$/.test(osd);

  // Determine actual hash and OSD from URL
  const hash = isOsdActuallyHash ? osd : hashParam;
  const urlOSD = isOsdActuallyHash ? 'TAURON' : (validOSDs.includes(osd as typeof validOSDs[number]) ? osd as typeof validOSDs[number] : 'TAURON');
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [formValues, setFormValues] = useState<CalculatorFormValues | null>(null);

  // Handle redirect to full calculator with pre-filled values
  const handleViewFullDetails = useCallback(() => {
    if (formValues) {
      navigate('/kalkulatory/podstawowy', {
        state: {
          sharedCalculation: formValues,
          fromSharedLink: true,
          existingShareHash: hash,
        },
        replace: false,
      });
    }
  }, [navigate, formValues, hash]);

  useEffect(() => {
    const loadSharedCalculation = async () => {
      if (!year || !hash) {
        setError('Nieprawidłowy link. Brak roku lub identyfikatora kalkulacji.');
        setLoading(false);
        return;
      }

      // Validate hash format (12 characters, Base62: alphanumeric only)
      if (hash.length !== 12 || !/^[A-Za-z0-9]{12}$/.test(hash)) {
        setError('Nieprawidłowy identyfikator kalkulacji. Hash musi mieć dokładnie 12 znaków alfanumerycznych.');
        setLoading(false);
        return;
      }

      // Parse year
      const yearNum = parseInt(year, 10);
      if (isNaN(yearNum) || yearNum < 2020 || yearNum > 2100) {
        setError('Nieprawidłowy rok kalkulacji.');
        setLoading(false);
        return;
      }

      try {
        const client = getAmplifyClient();

        // Query SharedCalculation by composite key (calculationHash + year)
        // CRITICAL: Use API Key auth for public shared links (no login required)
        const result = await client.models.SharedCalculation.get(
          {
            calculationHash: hash,
            year: yearNum,
          },
          {
            authMode: 'apiKey', // Public access - no authentication required
          }
        );

        if (!result.data) {
          setError(
            'Kalkulacja nie została znaleziona. Link może być nieprawidłowy lub kalkulacja wygasła.'
          );
          setLoading(false);
          return;
        }

        const sharedCalc = result.data;

        // Parse hourlyConfig if stored as JSON string
        let hourlyConfig: HourlyConfig | undefined;
        if (sharedCalc.hourlyConfig) {
          try {
            hourlyConfig =
              typeof sharedCalc.hourlyConfig === 'string'
                ? JSON.parse(sharedCalc.hourlyConfig)
                : sharedCalc.hourlyConfig;
          } catch {
            // Silent fail - use undefined hourlyConfig
            hourlyConfig = undefined;
          }
        }

        // Handle nullable monthly consumption
        const monthlyConsumption = (sharedCalc.monthlyConsumption || Array(12).fill(0))
          .map((v: number | null) => v ?? 0);

        // Map to CalculatorFormValues (including OSD for Multi-OSD support)
        // IMPORTANT: URL's OSD takes precedence over DB value for Multi-OSD support
        const values: CalculatorFormValues = {
          monthlyConsumption,
          currentTariff: (sharedCalc.currentTariff as TariffType) || 'G11',
          selectedOSD: urlOSD, // Use OSD from URL (not DB) for correct pricing
          householdSize: sharedCalc.householdSize || 3,
          hasHeatPump: sharedCalc.hasHeatPump || false,
          hasSolar: sharedCalc.hasSolar || false,
          hasEV: sharedCalc.hasEV || false,
          consumptionPattern: (sharedCalc.consumptionPattern as CalculatorFormValues['consumptionPattern']) || 'UNIFORM',
          hourlyConfig: hourlyConfig,
        };

        setFormValues(values);

        // Multi-OSD Support: Calculate results using correct OSD pricing
        // URL OSD takes precedence (already set in values.selectedOSD)
        const selectedOSD = values.selectedOSD as OSDCode || 'TAURON';

        // Build consumption pattern (use hourly config or default to uniform)
        // HourlyConfig values are already 0.0-1.0 fractions (sum should equal 1.0)
        // ConsumptionPattern expects multipliers where 1.0 = uniform distribution
        const pattern: ConsumptionPattern = values.hourlyConfig
          ? {
              weekday: values.hourlyConfig.weekday || Array(24).fill(1),
              weekend: values.hourlyConfig.weekend || Array(24).fill(1),
            }
          : UNIFORM_PATTERN;

        // Build OSD-specific pricing and calculate
        const osdPricing = buildOSDPricing(selectedOSD, yearNum);

        const annualResults = calculateAnnualComparison(
          values.monthlyConsumption,
          osdPricing,
          pattern,
          yearNum
        );

        // Transform to UI format using same logic as Calculator.tsx
        const previewResults = transformBackendResults(annualResults, selectedOSD as OSDMetaCode);

        setResults(previewResults);
        setLoading(false);

      } catch (err) {
        // Log detailed error for debugging
        console.error('[SharedCalculation] Error during calculation:', err);
        console.error('[SharedCalculation] Error stack:', (err as Error).stack);
        setError('Błąd podczas ładowania kalkulacji. Spróbuj ponownie później.');
        setLoading(false);
      }
    };

    void loadSharedCalculation();
  }, [year, hash, urlOSD]);

  // Error state
  if (error) {
    return (
      <Layout>
        <Container size="sm" py="xl">
          <Alert
            icon={<IconAlertCircle />}
            color="red"
            title="Nie udało się załadować kalkulacji"
            variant="filled"
          >
            <Text size="sm" mb="md">
              {error}
            </Text>
            <Text
              size="sm"
              component="a"
              href="/kalkulatory/podstawowy"
              style={{ textDecoration: 'underline', cursor: 'pointer', color: 'white' }}
            >
              Przejdź do kalkulatora
            </Text>
          </Alert>
        </Container>
      </Layout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Layout>
        <Container size="sm" py="xl">
          <Stack gap="lg">
            <Center>
              <Stack align="center" gap="md">
                <IconLoader size={48} style={{ animation: 'spin 1s linear infinite' }} />
                <Text size="lg" fw={500}>
                  Ładowanie udostępnionej kalkulacji...
                </Text>
                <Text size="sm" c="dimmed">
                  Za chwilę zobaczysz wyniki
                </Text>
              </Stack>
            </Center>

            <Skeleton height={150} radius="md" />
            <Skeleton height={200} radius="md" />
            <Skeleton height={100} radius="md" />
          </Stack>

          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </Container>
      </Layout>
    );
  }

  // Preview state - show results summary
  if (results && formValues) {
    return (
      <Layout>
        <SharedCalculationPreview
          results={results}
          currentTariff={formValues.currentTariff}
          onViewFullDetails={handleViewFullDetails}
        />
      </Layout>
    );
  }

  // Fallback
  return null;
}

/**
 * Transform backend calculation results to UI format
 *
 * Multi-OSD Support: Filters tariffs based on selected OSD availability.
 * Matches the logic in Calculator.tsx transformBackendResults.
 */
function transformBackendResults(
  annualResults: ReturnType<typeof calculateAnnualComparison>,
  selectedOSD: OSDMetaCode
): CalculationResults {
  // Get available tariffs for selected OSD
  const availableTariffs = getAvailableTariffsForOSD(selectedOSD);
  const isTariffAvailable = (tariffCode: string): boolean =>
    availableTariffs.some(t => t.toLowerCase() === tariffCode.toLowerCase());

  // Helper to transform a single tariff result
  const transformTariff = (tariffResult: typeof annualResults.g11) => ({
    tariffName: '',
    energyCost: tariffResult.totalProductionCost * 1.23,
    distributionCost: tariffResult.totalDistributionCost * 1.23,
    totalAnnualCost: tariffResult.totalCost,
    savings: 0,
    averageCostPerKwh: tariffResult.averageCostPerKwh,
    isRecommended: false,
  });

  // Build list of available tariffs with costs
  const availableCosts: { code: string; cost: number }[] = [];

  if (isTariffAvailable('G11')) {
    availableCosts.push({ code: 'G11', cost: annualResults.g11.totalCost });
  }
  if (isTariffAvailable('G12')) {
    availableCosts.push({ code: 'G12', cost: annualResults.g12.totalCost });
  }
  if (isTariffAvailable('G12w') && annualResults.g12w) {
    availableCosts.push({ code: 'G12w', cost: annualResults.g12w.totalCost });
  }
  if (isTariffAvailable('G12as') && annualResults.g12as) {
    availableCosts.push({ code: 'G12as', cost: annualResults.g12as.totalCost });
  }
  if (isTariffAvailable('G12n') && annualResults.g12n) {
    availableCosts.push({ code: 'G12n', cost: annualResults.g12n.totalCost });
  }
  if (isTariffAvailable('G12r') && annualResults.g12r) {
    availableCosts.push({ code: 'G12r', cost: annualResults.g12r.totalCost });
  }
  if (isTariffAvailable('G13') && annualResults.g13) {
    availableCosts.push({ code: 'G13', cost: annualResults.g13.totalCost });
  }
  if (isTariffAvailable('G13s') && annualResults.g13s) {
    availableCosts.push({ code: 'G13s', cost: annualResults.g13s.totalCost });
  }

  // Find cheapest and most expensive
  const cheapestCost = availableCosts.length > 0
    ? Math.min(...availableCosts.map(t => t.cost))
    : annualResults.g11.totalCost;
  const maxCost = availableCosts.length > 0
    ? Math.max(...availableCosts.map(t => t.cost))
    : annualResults.g11.totalCost;

  const recommendedTariff = availableCosts.length > 0
    ? availableCosts.reduce((min, curr) => curr.cost < min.cost ? curr : min).code
    : 'G11';

  const potentialSavings = maxCost - cheapestCost;

  // Build results object
  const results: Partial<CalculationResults> = {};

  if (isTariffAvailable('G11')) {
    const g11 = transformTariff(annualResults.g11);
    g11.tariffName = 'G11';
    g11.savings = cheapestCost - annualResults.g11.totalCost;
    g11.isRecommended = recommendedTariff === 'G11';
    results.g11 = g11;
  }

  if (isTariffAvailable('G12')) {
    const g12 = transformTariff(annualResults.g12);
    g12.tariffName = 'G12';
    g12.savings = cheapestCost - annualResults.g12.totalCost;
    g12.isRecommended = recommendedTariff === 'G12';
    results.g12 = g12;
  }

  if (isTariffAvailable('G12w') && annualResults.g12w) {
    const g12w = transformTariff(annualResults.g12w);
    g12w.tariffName = 'G12w';
    g12w.savings = cheapestCost - annualResults.g12w.totalCost;
    g12w.isRecommended = recommendedTariff === 'G12w';
    results.g12w = g12w;
  }

  if (isTariffAvailable('G12as') && annualResults.g12as) {
    const g12as = transformTariff(annualResults.g12as);
    g12as.tariffName = 'G12as';
    g12as.savings = cheapestCost - annualResults.g12as.totalCost;
    g12as.isRecommended = recommendedTariff === 'G12as';
    results.g12as = g12as;
  }

  if (isTariffAvailable('G12n') && annualResults.g12n) {
    const g12n = transformTariff(annualResults.g12n);
    g12n.tariffName = 'G12n';
    g12n.savings = cheapestCost - annualResults.g12n.totalCost;
    g12n.isRecommended = recommendedTariff === 'G12n';
    results.g12n = g12n;
  }

  if (isTariffAvailable('G12r') && annualResults.g12r) {
    const g12r = transformTariff(annualResults.g12r);
    g12r.tariffName = 'G12r';
    g12r.savings = cheapestCost - annualResults.g12r.totalCost;
    g12r.isRecommended = recommendedTariff === 'G12r';
    results.g12r = g12r;
  }

  if (isTariffAvailable('G13') && annualResults.g13) {
    const g13 = transformTariff(annualResults.g13);
    g13.tariffName = 'G13';
    g13.savings = cheapestCost - annualResults.g13.totalCost;
    g13.isRecommended = recommendedTariff === 'G13';
    results.g13 = g13;
  }

  if (isTariffAvailable('G13s') && annualResults.g13s) {
    const g13s = transformTariff(annualResults.g13s);
    g13s.tariffName = 'G13s';
    g13s.savings = cheapestCost - annualResults.g13s.totalCost;
    g13s.isRecommended = recommendedTariff === 'G13s';
    results.g13s = g13s;
  }

  results.recommendedTariff = recommendedTariff as TariffType;
  results.potentialSavings = potentialSavings;
  results.savingsPercent = maxCost > 0 ? (potentialSavings / maxCost) * 100 : 0;

  return results as CalculationResults;
}
